import React, { useEffect } from 'react';
import { useStudentTheme } from '@context/StudentThemeContext';

interface ClassicDarkScopeProps {
  children: React.ReactNode;
}

/**
 * Re-supplies the `.student-dark` ancestor that CLASSIC Student Portal
 * components (`StudentNotebook`, `StudentProfile`) rely on for their dark
 * styling — their Tailwind `dark:` variant and the `studentDarkTheme.scss`
 * utility overrides (`.bg-white`, `.text-gray-*`, …) are all gated on it.
 * CLASSIC's `StudentLayout` adds that class; the Journey shell uses
 * `.sp-theme-dark` instead, so a CLASSIC page dropped into a Journey route
 * would render light-on-dark without this wrapper.
 *
 * - The wrapper `<div>` covers the in-tree markup.
 * - It also mirrors the class onto `document.body` while mounted, because the
 *   shared `Modal` (StudentProfile's "Preview PDF") renders through a portal to
 *   `document.body` and would otherwise pop up fully light over the dark shell.
 *   No Journey `.sp-*` chrome uses the raw Tailwind colour utilities that
 *   `.student-dark` overrides, so the shell is unaffected.
 *
 * Light mode: a transparent passthrough (one plain `<div>`, no class, no body flag).
 */
export const ClassicDarkScope: React.FC<ClassicDarkScopeProps> = ({ children }) => {
  const { theme } = useStudentTheme();
  const dark = theme === 'dark';

  useEffect(() => {
    if (!dark) return;
    const alreadyThere = document.body.classList.contains('student-dark');
    if (!alreadyThere) document.body.classList.add('student-dark');
    return () => {
      if (!alreadyThere) document.body.classList.remove('student-dark');
    };
  }, [dark]);

  return <div className={dark ? 'student-dark' : undefined}>{children}</div>;
};
